import * as THREE from 'three';
import { boneNameIndex, normalizeBoneName } from './boneNames.js';

/**
 * Binds a set of spheres to a fighter's bones, so the volumes are posed by the
 * animation rather than by the game. This is the bridge between the rig and
 * `strikeLayer.js`: three.js lives on this side of it, and what crosses over
 * is a plain `{x, y, z, radius}` the rule layer can read without importing
 * anything.
 *
 * Bones are matched by normalised name, so one spec fits any spelling of a
 * Mixamo rig. A spec whose bone is missing — an unrigged model that fell back
 * to the capsule — rides the fighter's own transform instead, so a stand-in is
 * still hittable rather than being a ghost punches pass straight through.
 */

/** Shared unit sphere; each volume scales it. Never disposed, never mutated. */
const SPHERE = new THREE.SphereGeometry(1, 12, 8);

function wireMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    wireframe: true,
    transparent: true,
    opacity,
    // Drawn through the body on purpose: a volume you can only see when it has
    // left the mesh is a volume you cannot debug.
    depthTest: false,
  });
}

export function createBoneVolumes(
  fighter,
  specs,
  { color = 0xffffff, visible = false, name = 'volumes', onWarn = () => {} } = {}
) {
  let root = fighter.object3D;
  const boneFinder = (body) => {
    const index = body.skeleton ? boneNameIndex(body.skeleton.bones) : new Map();
    const byName = new Map();
    for (const bone of body.skeleton?.bones ?? []) byName.set(bone.name, bone);
    return (wanted) => byName.get(index.get(normalizeBoneName(wanted))) ?? null;
  };
  let find = boneFinder(fighter);

  const debug = new THREE.Group();
  debug.name = `${name}-debug`;
  debug.visible = visible;

  const cool = wireMaterial(color, 0.6);
  const hot = wireMaterial(0xff4d4d, 0.95);

  function volumeFor(spec) {
    const bone = find(spec.bone);
    if (!bone) onWarn(`no bone "${spec.bone}" on ${root.name}; ${spec.name} rides the root`);

    const mesh = new THREE.Mesh(SPHERE, cool);
    mesh.name = `${name}:${spec.name}`;
    mesh.scale.setScalar(spec.radius);
    mesh.renderOrder = 999;
    debug.add(mesh);

    return {
      ...spec,
      boneSpec: spec.bone,
      bone,
      mesh,
      offset: new THREE.Vector3(...(spec.offset ?? [0, 0, 0])),
      x: 0,
      y: 0,
      z: 0,
    };
  }

  // One list for the life of the fighter: a strike layer holds it, so a new
  // body's volumes are written into it rather than handed over as another.
  const volumes = specs.map(volumeFor);
  // First of each name: a region fitted with two spheres answers to one name.
  const lookup = new Map();
  const index = () => {
    lookup.clear();
    for (const volume of volumes) if (!lookup.has(volume.name)) lookup.set(volume.name, volume);
  };
  index();
  const scratch = new THREE.Vector3();
  let live = null;

  return {
    /** Wireframe spheres in world space — add this to the scene, not the rig. */
    object3D: debug,
    list: volumes,
    get: (which) => lookup.get(which) ?? null,

    get visible() {
      return debug.visible;
    },
    setVisible(next) {
      debug.visible = Boolean(next);
    },

    /**
     * Reddens one volume — the fist during a strike's active window. Driven by
     * state rather than by an event, so there is no timer to clear and nothing
     * to leave stuck on.
     */
    /**
     * Re-seats every volume on a new body's bones. The volumes themselves —
     * the list a strike layer resolves against, the wireframes in the scene —
     * are the same objects, so nothing that holds them has to be told.
     */
    rebind(next) {
      root = next.object3D;
      find = boneFinder(next);
      for (const volume of volumes) {
        volume.bone = find(volume.boneSpec);
      }
      for (const volume of volumes) {
        if (!volume.bone) onWarn(`no bone "${volume.boneSpec}" on ${root.name}; ${volume.name} rides the root`);
      }
    },

    /**
     * A body whose volumes are its own: fitted hurtboxes, more or fewer of them
     * and each sized for this body. Written into the same `list`, with the
     * wireframes rebuilt in the same group, so whatever holds either keeps
     * holding the right thing. Seat them with `rebind` after.
     */
    respec(next) {
      for (const volume of volumes) volume.mesh.removeFromParent();
      volumes.length = 0;
      volumes.push(...next.map(volumeFor));
      index();
      live = null;
    },

    setLive(which) {
      if (live === which) return;
      live = which;
      for (const volume of volumes) {
        volume.mesh.material = volume.name === which ? hot : cool;
      }
    },

    /**
     * Re-reads every volume's world position from its bone. The skeleton is
     * posed by the mixer, but its world matrices are only refreshed at render
     * time, so this forces them current — otherwise every hit test would be
     * resolving against the previous frame's pose.
     */
    update() {
      root.updateMatrixWorld(true);
      for (const volume of volumes) {
        scratch.copy(volume.offset);
        scratch.applyMatrix4(volume.bone ? volume.bone.matrixWorld : root.matrixWorld);
        volume.x = scratch.x;
        volume.y = scratch.y;
        volume.z = scratch.z;
        if (debug.visible) volume.mesh.position.copy(scratch);
      }
    },

    dispose() {
      debug.removeFromParent();
      cool.dispose();
      hot.dispose();
      lookup.clear();
    },
  };
}
